/**
 * Local cache for user avatars (`{AUTH_BASE}/avatars/{id}`), silently
 * refreshed when the picture behind the URL changes.
 *
 * Why: the IdP serves every avatar from a stable URL and answers with a 302
 * (`max-age=300`) to an immutable object whose key is random per upload. The
 * chat broadcasts carry no profile events, so a re-uploaded avatar only ever
 * changed on screen after a remount plus the WebView's own cache expiry. The
 * storage ETag behind the redirect IS an exact change signal — a HEAD through
 * the Rust HTTP command (no CORS, follows the redirect) returns it without
 * downloading the image. This module keeps the bytes (IndexedDB + a memory
 * map of `blob:` object URLs), revalidates by ETag on a TTL / on wake, and
 * publishes the object URL into `store/avatarStore.js` for `UserAvatar`.
 *
 * Gate: everything network-related is behind `isTauri()`. In the browser dev
 * server the IdP route has no CORS and `UserAvatar` keeps its network `<img>`.
 * Failure semantics: nothing here ever throws to a caller — a failed HEAD /
 * GET / IndexedDB op logs at `console.debug` and leaves whatever is cached.
 *
 * Patterns copied (not generalised): the Map + in-flight dedupe of
 * `utils/decryptedMediaCache.js`, the IndexedDB open/op of
 * `services/documentAttachmentOutbox.js`, the focus / visibility listeners of
 * `hooks/useSyncEngine.js`.
 */
import { isTauri, sendRequestViaTauri } from '../utils/tauri';
import { AUTH_BASE } from './oidcService';
import { useAvatarStore } from '../store/avatarStore';

export const AVATAR_TTL_MS = 10 * 60 * 1000;
export const AVATAR_MAX_INFLIGHT = 4;
export const AVATAR_PRUNE_AFTER_MS = 30 * 24 * 60 * 60 * 1000;
export const AVATAR_FOCUS_THROTTLE_MS = 60 * 1000;

const SEEN_BUMP_MS = 24 * 60 * 60 * 1000; // persist `seenAt` at most once a day
const HEAD_TIMEOUT_SECS = 15;
const GET_TIMEOUT_SECS = 20;

const IDB_NAME = 'apiboo-avatars';
const IDB_VERSION = 1;
const IDB_STORE = 'avatars';

/* ── state ──────────────────────────────────────────────────────────────── */

// id -> { etag, contentType, objectUrl, blob, checkedAt, seenAt }. The blob is
// kept so a same-ETag revalidation can `put` the record back with a fresh
// `checkedAt` without a round-trip to IndexedDB.
const entries = new Map();
const inflight = new Map(); // id -> Promise (one HTTP flow per id at a time)

let adapterOverride = null;
let webAdapter = null;
let started = false;
let intervalId = null;
let wakeHandler = null;
let lastWakeAt = 0;

// Concurrency cap: opening a 50-member channel must not fire 50 requests.
let activeSlots = 0;
const waitingForSlot = [];

const now = () => Date.now();
const avatarUrl = (id) => `${AUTH_BASE}/avatars/${id}`;

function debug(...args) {
  try {
    console.debug('[avatarCache]', ...args);
  } catch {
    // ignore
  }
}

/* ── helpers ────────────────────────────────────────────────────────────── */

function headerOf(headers, name) {
  if (!headers || typeof headers !== 'object') return null;
  const want = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === want) {
      const value = headers[key];
      return Array.isArray(value) ? (value[0] ?? null) : (value ?? null);
    }
  }
  return null;
}

// Inverse of `arrayBufferToBase64` (utils/tauriAxiosAdapter.js), kept local.
function base64ToBytes(b64) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function acquireSlot() {
  if (activeSlots < AVATAR_MAX_INFLIGHT) {
    activeSlots += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => waitingForSlot.push(resolve));
}

function releaseSlot() {
  const next = waitingForSlot.shift();
  if (next) next(); // hand the slot over; activeSlots is unchanged
  else activeSlots -= 1;
}

async function withSlot(fn) {
  await acquireSlot();
  try {
    return await fn();
  } finally {
    releaseSlot();
  }
}

function exclusive(id, fn) {
  if (inflight.has(id)) return inflight.get(id);
  const promise = (async () => {
    try {
      await fn();
    } catch (err) {
      debug('flow failed', id, err);
    } finally {
      inflight.delete(id);
    }
  })();
  inflight.set(id, promise);
  return promise;
}

function publish(id, objectUrl) {
  useAvatarStore.getState().setAvatarSrc(id, objectUrl);
}

/* ── persistence ────────────────────────────────────────────────────────── */

const NOOP_ADAPTER = {
  async get() {
    return null;
  },
  async put() {},
  async list() {
    return [];
  },
  async remove() {},
};

// Web adapter — IndexedDB. Real-IDB behaviour is not exercised in jsdom (no
// IndexedDB there); tests inject a memory adapter. Copied from
// documentAttachmentOutbox.createWebOutboxAdapter, own store / key.
function createWebAvatarAdapter() {
  let dbPromise = null;

  function openDb() {
    if (!dbPromise) {
      dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_NAME, IDB_VERSION);
        req.onupgradeneeded = () => {
          const db = req.result;
          if (!db.objectStoreNames.contains(IDB_STORE)) {
            db.createObjectStore(IDB_STORE, { keyPath: 'userId' });
          }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => {
          dbPromise = null;
          reject(req.error || new Error('IndexedDB open failed'));
        };
      });
    }
    return dbPromise;
  }

  function op(mode, run) {
    return openDb().then(
      (db) =>
        new Promise((resolve, reject) => {
          const tx = db.transaction(IDB_STORE, mode);
          const req = run(tx.objectStore(IDB_STORE));
          req.onsuccess = () => resolve(req.result);
          req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
        })
    );
  }

  return {
    get: (id) => op('readonly', (store) => store.get(id)).then((r) => r ?? null),
    put: (record) => op('readwrite', (store) => store.put(record)),
    list: () => op('readonly', (store) => store.getAll()).then((r) => r || []),
    remove: (id) => op('readwrite', (store) => store.delete(id)),
  };
}

function getAdapter() {
  if (adapterOverride) return adapterOverride;
  if (!webAdapter) {
    webAdapter = typeof indexedDB === 'undefined' ? NOOP_ADAPTER : createWebAvatarAdapter();
  }
  return webAdapter;
}

// Every persistence call goes through here: IDB unavailable / failing means
// memory-only, silently.
async function safe(label, fn) {
  try {
    return await fn(getAdapter());
  } catch (err) {
    debug(label, 'failed', err);
    return null;
  }
}

function persistEntry(id, entry) {
  return safe('put', (adapter) =>
    adapter.put({
      userId: id,
      etag: entry.etag,
      contentType: entry.contentType,
      blob: entry.blob,
      checkedAt: entry.checkedAt,
      seenAt: entry.seenAt,
    })
  );
}

function touch(id, entry) {
  if (now() - (entry.seenAt || 0) >= SEEN_BUMP_MS) {
    entry.seenAt = now();
    persistEntry(id, entry);
  }
}

async function prune() {
  const records = await safe('list', (adapter) => adapter.list());
  if (!Array.isArray(records)) return;
  const cutoff = now() - AVATAR_PRUNE_AFTER_MS;
  for (const record of records) {
    if (record && record.userId != null && (record.seenAt || 0) < cutoff) {
      await safe('remove', (adapter) => adapter.remove(String(record.userId)));
    }
  }
}

/* ── network flows (internal, run inside `exclusive`) ───────────────────── */

function blobFromResponse(res) {
  const contentType = headerOf(res.headers, 'content-type') || '';
  if (typeof res.data_base64 === 'string' && res.data_base64.length > 0) {
    return new Blob([base64ToBytes(res.data_base64)], { type: contentType });
  }
  if (contentType.toLowerCase().startsWith('image/svg') && typeof res.data === 'string') {
    return new Blob([res.data], { type: 'image/svg+xml' });
  }
  return null;
}

async function fetchAndStoreNow(id) {
  let res;
  try {
    res = await withSlot(() =>
      sendRequestViaTauri({
        method: 'GET',
        url: avatarUrl(id),
        headers: {},
        timeout_secs: GET_TIMEOUT_SECS,
      })
    );
  } catch (err) {
    debug('GET failed', id, err);
    return;
  }
  // An avatar is never large enough for the temp-file path; a non-200 (the
  // IdP's 404 for an unknown user, a 5xx) keeps whatever is cached. No
  // back-off here on purpose — the wake throttle and the interval bound the
  // retry rate.
  if (!res || res.status !== 200 || res.temp_file) return;
  const blob = blobFromResponse(res);
  if (!blob) return;

  const objectUrl = URL.createObjectURL(blob);
  const prev = entries.get(id);
  const entry = {
    etag: headerOf(res.headers, 'etag'), // may be null → next revalidation refetches
    contentType: blob.type,
    objectUrl,
    blob,
    checkedAt: now(),
    seenAt: now(),
  };
  entries.set(id, entry);
  // Publish FIRST, revoke the previous URL AFTER: the <img> has already
  // swapped to the new src by the time the old one goes away, so no flash.
  publish(id, objectUrl);
  if (prev && prev.objectUrl && prev.objectUrl !== objectUrl) {
    URL.revokeObjectURL(prev.objectUrl);
  }
  await persistEntry(id, entry);
}

async function revalidateNow(id) {
  const entry = entries.get(id);
  if (!entry) return fetchAndStoreNow(id);
  let res;
  try {
    res = await withSlot(() =>
      sendRequestViaTauri({
        method: 'HEAD',
        url: avatarUrl(id),
        headers: {},
        timeout_secs: HEAD_TIMEOUT_SECS,
      })
    );
  } catch (err) {
    // Offline / timeout: back off for a TTL, keep what is cached.
    debug('HEAD failed', id, err);
    entry.checkedAt = now();
    await persistEntry(id, entry);
    return;
  }
  const etag = res ? headerOf(res.headers, 'etag') : null;
  const ok = res && res.status >= 200 && res.status < 300;
  if (!ok || !etag || etag === entry.etag) {
    // Unchanged (or nothing to compare against): nothing on screen changes.
    entry.checkedAt = now();
    await persistEntry(id, entry);
    return;
  }
  return fetchAndStoreNow(id);
}

async function loadNow(id) {
  const record = await safe('get', (adapter) => adapter.get(id));
  if (record && record.blob) {
    const objectUrl = URL.createObjectURL(record.blob);
    const entry = {
      etag: record.etag ?? null,
      contentType: record.contentType || record.blob.type || '',
      objectUrl,
      blob: record.blob,
      checkedAt: record.checkedAt || 0,
      seenAt: record.seenAt || 0,
    };
    entries.set(id, entry);
    publish(id, objectUrl);
    touch(id, entry);
    if (now() - entry.checkedAt > AVATAR_TTL_MS) await revalidateNow(id);
    return;
  }
  await fetchAndStoreNow(id);
}

/* ── background revalidation ────────────────────────────────────────────── */

function onWake() {
  if (typeof document !== 'undefined' && document.hidden) return;
  if (now() - lastWakeAt < AVATAR_FOCUS_THROTTLE_MS) return;
  lastWakeAt = now();
  revalidateKnown();
}

function startBackground() {
  if (started) return;
  started = true;
  lastWakeAt = now();
  intervalId = setInterval(() => {
    revalidateKnown();
  }, AVATAR_TTL_MS);
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    wakeHandler = onWake;
    window.addEventListener('focus', wakeHandler);
    document.addEventListener('visibilitychange', wakeHandler);
  }
  prune().catch((err) => debug('prune failed', err));
}

/* ── public API ─────────────────────────────────────────────────────────── */

/**
 * Make sure `userId`'s avatar is cached and published; revalidate when the
 * cached copy is older than the TTL. Never rejects.
 */
export function ensureAvatar(userId) {
  if (userId == null || !isTauri()) return Promise.resolve();
  const id = String(userId);
  startBackground();
  const entry = entries.get(id);
  if (entry) {
    touch(id, entry);
    if (now() - entry.checkedAt > AVATAR_TTL_MS) return revalidate(id);
    return Promise.resolve();
  }
  return exclusive(id, () => loadNow(id));
}

/** HEAD-revalidate one cached avatar (ignores the TTL). Never rejects. */
export function revalidate(userId) {
  if (userId == null || !isTauri()) return Promise.resolve();
  const id = String(userId);
  return exclusive(id, () => revalidateNow(id));
}

/**
 * Force a fresh GET (skips HEAD and TTL) — used for the signed-in user's own
 * avatar right after an upload, so the new picture appears everywhere at once.
 */
export function refreshAvatar(userId) {
  if (userId == null || !isTauri()) return Promise.resolve();
  const id = String(userId);
  startBackground();
  return exclusive(id, () => fetchAndStoreNow(id));
}

/** Revalidate every avatar rendered this session whose check is past the TTL. */
export function revalidateKnown() {
  if (!isTauri()) return Promise.resolve();
  const due = [];
  for (const [id, entry] of entries) {
    if (now() - entry.checkedAt > AVATAR_TTL_MS) due.push(revalidate(id));
  }
  return Promise.all(due).then(() => undefined);
}

/* ── test seams ─────────────────────────────────────────────────────────── */

export function __setAvatarPersistenceForTests(adapter) {
  adapterOverride = adapter || null;
}

export function __resetAvatarCacheForTests() {
  for (const entry of entries.values()) {
    try {
      if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
    } catch {
      // ignore
    }
  }
  entries.clear();
  inflight.clear();
  waitingForSlot.length = 0;
  activeSlots = 0;
  if (intervalId) clearInterval(intervalId);
  intervalId = null;
  if (wakeHandler && typeof window !== 'undefined') {
    window.removeEventListener('focus', wakeHandler);
    document.removeEventListener('visibilitychange', wakeHandler);
  }
  wakeHandler = null;
  started = false;
  lastWakeAt = 0;
  adapterOverride = null;
  webAdapter = null;
}
